<template>
  <div>
    <div class="reports-heading">
      <h3>Annual reports</h3>
      <button class="btn btn-success" :disabled="!trackingId" @click="showModal = true">
        Submit annual report
      </button>
    </div>
    <p v-if="loading" role="status">Loading annual reports...</p>
    <div v-else-if="error" role="alert">
      <p>{{ error }}</p>
      <button v-if="trackingId" class="btn btn-outline-success" @click="loadReports">
        Try again
      </button>
    </div>
    <p v-else-if="!reports.length">No annual reports have been submitted for this organization.</p>
    <div v-else class="table-responsive">
      <table class="table">
        <caption class="sr-only">
          Organization annual reports
        </caption>
        <thead>
          <tr>
            <th scope="col">Title</th>
            <th scope="col">Year</th>
            <th scope="col">Status</th>
            <th scope="col">Report file</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(report, index) in reports" :key="report.id || index">
            <td>{{ report.title || report.report_title || 'Untitled report' }}</td>
            <td>{{ report.year || report.reporting_year || 'Not provided' }}</td>
            <td>
              {{
                report.approved === true
                  ? 'Approved'
                  : report.approved === false
                  ? 'Pending'
                  : report.status || 'Submitted'
              }}
            </td>
            <td>
              <a
                v-if="reportUrl(report)"
                :href="reportUrl(report)"
                target="_blank"
                rel="noopener noreferrer"
                >View report</a
              ><span v-else>Not provided</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <public-annual-report-modal
      :visible="showModal"
      :tracking-id="trackingId"
      :is-submitting="submitting"
      @close="showModal = false"
      @submit-report="submitReport"
    />
  </div>
</template>

<script>
import submitAnnualReportByTrackingId from '../../config/api-modules/api.reports';
import { getOrganizationByTrackingId } from '../../config/api-modules/api.organization';
import PublicAnnualReportModal from '../modals/PublicAnnualReportModal.vue';

export default {
  name: 'OrganizationAnnualReports',
  components: { PublicAnnualReportModal },
  props: {
    trackingId: { type: String, required: true },
    annualReports: { type: Array, default: () => [] },
  },
  data() {
    return {
      reports: [],
      loading: false,
      error: '',
      showModal: false,
      submitting: false,
      requestId: 0,
    };
  },
  watch: {
    annualReports: {
      immediate: true,
      handler(reports) {
        this.reports = (reports || []).filter(Boolean);
      },
    },
  },
  beforeDestroy() {
    this.requestId += 1;
  },
  methods: {
    reportUrl(report) {
      const value = report.report_file_url || report.report_file || report.file_url || report.file;
      if (typeof value !== 'string' || !value) return '';
      try {
        const url = new URL(value, process.env.VUE_APP_API_BASE_URL || window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
      } catch {
        return '';
      }
    },
    async loadReports() {
      this.requestId += 1;
      const { requestId } = this;
      this.reports = [];
      this.error = '';
      this.loading = false;
      if (!this.trackingId) {
        this.error = 'Annual reports are unavailable because this record has no tracking ID.';
        return;
      }
      this.loading = true;
      try {
        const { data } = await getOrganizationByTrackingId(this.trackingId);
        if (requestId !== this.requestId) return;
        const payload = data?.data || data;
        const reports = payload?.annual_reports ?? [];
        if (!Array.isArray(reports)) throw new Error('Unexpected reports response');
        this.reports = reports.filter(Boolean);
        this.$emit('reports-loaded', this.reports);
      } catch (error) {
        if (requestId === this.requestId)
          this.error =
            error.response?.data?.detail || 'Unable to load annual reports. Please try again.';
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async submitReport(payload) {
      if (this.submitting || !this.trackingId) return;
      const { requestId } = this;
      this.submitting = true;
      const fields = { ...payload, tracking_id: this.trackingId };
      let data = fields;
      if (payload.report_file instanceof File) {
        data = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') data.append(key, value);
        });
      } else {
        delete fields.report_file;
      }
      try {
        await submitAnnualReportByTrackingId(data);
        if (requestId !== this.requestId) return;
        this.showModal = false;
        this.$toast.show({ type: 'SUCCESS', message: 'Annual report submitted.' });
        this.loadReports();
      } catch (error) {
        if (requestId === this.requestId)
          this.$toast.show({
            type: 'ERROR',
            message:
              error.response?.data?.detail ||
              error.response?.data?.message ||
              'Unable to submit the annual report. Please try again.',
          });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.reports-heading {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}
h3 {
  margin: 0;
  font-size: 1.12rem;
  font-weight: 700;
}
table {
  min-width: 540px;
}
a {
  color: #2f7149;
  text-decoration: underline;
}
</style>
